import axios, { isAxiosError } from 'axios';
import { createAction, createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { createEntitySlice, serializeAxiosError, EntityState } from '../../shared/reducers/reducer.utils';
import { cleanEntity } from '../../shared/util/entity-utils/entity-utils';
import {
    applyQuoEchoPasswordStrippersForEmpresaPutJson,
} from './empresa/empresa-config-import.shared';
import { getProveedores, getServicios } from '../empresa.reducer';
import {
    parseProveedorEmpresaConfig,
    type ProveedorEmpresaConfigApi,
} from './proveedor/proveedor-config-preview';

export type GrupoEmpresarialState = EntityState<any> & {
    empresas: any[];
    proveedores: any[];
    proveedorConfig: ProveedorEmpresaConfigApi | null;
    proveedorConfigLoading: boolean;
    proveedorConfigProveedorId: number | null;
};

function proveedoresFromApiPayload(payload: { data: unknown }): any[] {
    const data = payload.data;
    if (Array.isArray(data)) return data;
    if (data != null && typeof data === 'object') {
        const body = data as { content?: unknown[]; data?: unknown[] };
        if (Array.isArray(body.content)) return body.content;
        if (Array.isArray(body.data)) return body.data;
    }
    return [];
}

const initialState: GrupoEmpresarialState = {
    loading: false,
    errorMessage: null,
    entities: [],
    empresas: [],
    proveedores: [],
    entity: {},
    updating: false,
    totalItems: 0,
    updateSuccess: false,
    proveedorConfig: null,
    proveedorConfigLoading: false,
    proveedorConfigProveedorId: null,
};

const apiUrl = 'api/grupo-empresarials';

export const resetProveedorConfig = createAction('grupoEmpresarial/reset_proveedor_config');

/** Marca en fila local (solo UI); el binario va en una parte multipart cuyo nombre de campo es un UUID, el mismo en el mapa JSON `archivosSat` / `archivosSellos`. */
export const EMPRESA_CONFIG_UPLOAD_FILE_KEY = '__uploadFile';

function strVal(v: unknown): string {
    if (v == null) return '';
    return String(v);
}

/**
 * Valor JSON por parte de archivo (misma clave UUID que el campo multipart del binario):
 * SAT y Sellos usan la misma forma `{ id, nombreArchivo, archivoSAT }`.
 */
export type EmpresaConfigArchivoMultipartMapValue = {
    id: number | null;
    nombreArchivo: string;
    archivoSAT: Record<string, unknown>;
};

function normalizeArchivoRowId(row: Record<string, unknown>): number | null {
    const rowId = row.id;
    if (rowId == null || rowId === '') return null;
    const n = Number(rowId);
    return Number.isFinite(n) ? n : null;
}

/**
 * Objeto `archivoSAT` en mapas multipart: toma catálogo de la fila (archivoSAT, archivoSello legado, etc.).
 */
function archivoCatalogNestedForMultipartRow(row: Record<string, unknown>): Record<string, unknown> {
    const cat = row.archivoSAT ?? row.archivoSat ?? row.archivoSello ?? row.archivoSellos;
    const nombreArchivo = strVal(row.nombreArchivo).trim();
    if (cat && typeof cat === 'object' && !Array.isArray(cat)) {
        return { ...(cat as Record<string, unknown>) };
    }
    const tid = row.tipoArchivo;
    if (tid != null && tid !== '') {
        return { id: Number(tid), descripcion: nombreArchivo || '—' };
    }
    return { id: 1, descripcion: nombreArchivo || '—' };
}

function archivoMultipartMapEntry(row: Record<string, unknown>): EmpresaConfigArchivoMultipartMapValue {
    const nombreArchivo = strVal(row.nombreArchivo).trim();
    const id = normalizeArchivoRowId(row);
    return { id, nombreArchivo, archivoSAT: archivoCatalogNestedForMultipartRow(row) };
}

type UploadFilePart = { key: string; file: File };

function newUploadPartKey(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * PUT …/config en multipart/form-data:
 * - `config`: JSON principal (sin __uploadFile).
 * - `archivosSat` / `archivosSellos`: JSON, cada clave (UUID) → { id, nombreArchivo, archivoSAT }; parte archivo con el mismo nombre de campo.
 */
function buildEmpresaConfigPutFormData(config: Record<string, unknown>): FormData {
    const cleaned = cleanEntity(config) as Record<string, unknown>;
    const satCatalog: Record<string, EmpresaConfigArchivoMultipartMapValue> = {};
    const selCatalog: Record<string, EmpresaConfigArchivoMultipartMapValue> = {};
    const fileParts: UploadFilePart[] = [];

    const mapRows = (arr: unknown, cat: Record<string, EmpresaConfigArchivoMultipartMapValue>): unknown[] => {
        if (!Array.isArray(arr)) return [];
        const out: unknown[] = [];
        for (const row of arr) {
            if (!row || typeof row !== 'object') {
                out.push(omitUploadFieldsDeep(row));
                continue;
            }
            const r = row as Record<string, unknown>;
            const f = r[EMPRESA_CONFIG_UPLOAD_FILE_KEY];
            if (f instanceof File) {
                const key = newUploadPartKey();
                cat[key] = archivoMultipartMapEntry(r);
                fileParts.push({ key, file: f });
                const { [EMPRESA_CONFIG_UPLOAD_FILE_KEY]: _u, ...rest } = r;
                out.push(omitUploadFieldsDeep(rest));
            } else {
                out.push(omitUploadFieldsDeep(row));
            }
        }
        return out;
    };

    const archivosSat = mapRows(cleaned.archivosSat, satCatalog);
    const archivosSellos = mapRows(cleaned.archivosSellos, selCatalog);

    const forJson = omitUploadFieldsDeep({
        ...cleaned,
        archivosSat,
        archivosSellos,
    }) as Record<string, unknown>;
    applyQuoEchoPasswordStrippersForEmpresaPutJson(forJson);

    const fd = new FormData();
    fd.append('config', new Blob([JSON.stringify(forJson)], { type: 'application/json' }));
    if (Object.keys(satCatalog).length > 0) {
        fd.append('archivosSat', new Blob([JSON.stringify(satCatalog)], { type: 'application/json' }));
    }
    if (Object.keys(selCatalog).length > 0) {
        fd.append('archivosSellos', new Blob([JSON.stringify(selCatalog)], { type: 'application/json' }));
    }
    for (const { key, file } of fileParts) {
        fd.append(key, file, file.name);
    }
    return fd;
}

function omitUploadFieldsDeep(value: unknown): unknown {
    if (value == null) return value;
    if (typeof value !== 'object') return value;
    if (value instanceof File) return undefined;
    if (Array.isArray(value)) return value.map(omitUploadFieldsDeep);
    const obj = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
        if (k === EMPRESA_CONFIG_UPLOAD_FILE_KEY) continue;
        next[k] = omitUploadFieldsDeep(v);
    }
    return next;
}

// Actions

export const getEntities = createAsyncThunk(
    'grupoEmpresarial/fetch_entity_list',
    async ({ page, size, sort, query }: any) => {
        const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}&query=${query}`;
        return axios.get<any[]>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const getEmpresasByGrupo = createAsyncThunk(
    'grupoEmpresarial/empresas',
    async ({ id, page, size, sort }: any) => {
        const requestUrl = `${apiUrl}/${id}/empresa?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}`;
        return axios.get<any[]>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const createEmpresaEnGrupo = createAsyncThunk(
    'grupoEmpresarial/create_empresa_en_grupo',
    async ({ grupoId, empresa }: { grupoId: string | number; empresa: { rfc: string; razonSocial: string } }) => {
        return axios.post<any>(`${apiUrl}/${grupoId}/empresa`, cleanEntity(empresa));
    },
    { serializeError: serializeAxiosError },
);

export const updateEmpresaEnGrupo = createAsyncThunk(
    'grupoEmpresarial/update_empresa_en_grupo',
    async ({
        grupoId,
        empresaId,
        empresa,
    }: {
        grupoId: string | number;
        empresaId: string | number;
        empresa: Record<string, unknown>;
    }) => {
        return axios.put<any>(`${apiUrl}/${grupoId}/empresa/${empresaId}`, cleanEntity(empresa));
    },
    { serializeError: serializeAxiosError },
);

/** Config detallada para el formulario de edición (info + listas SAT / QUO). */
export const getEmpresaConfigEnGrupo = createAsyncThunk(
    'grupoEmpresarial/empresa_config',
    async ({ grupoId, empresaId }: { grupoId: string | number; empresaId: string | number }) => {
        return axios.get<any>(`${apiUrl}/${grupoId}/empresa/${empresaId}/config`);
    },
    { serializeError: serializeAxiosError },
);

/** Proveedores del grupo (sin empresa seleccionada). */
export const getProveedoresByGrupo = createAsyncThunk(
    'grupoEmpresarial/fetch_proveedores_grupo',
    async (grupoId: string | number) => {
        return axios.get<any>(`${apiUrl}/${grupoId}/proveedor`);
    },
    { serializeError: serializeAxiosError },
);

/** Config de empresa bajo un proveedor (compañía) asignado. */
export const getProveedorConfigEnGrupo = createAsyncThunk(
    'grupoEmpresarial/proveedor_config',
    async ({
        grupoId,
        empresaId,
        proveedorId,
    }: {
        grupoId: string | number;
        empresaId: string | number;
        proveedorId: string | number;
    }) => {
        return axios.get<any>(
            `${apiUrl}/${grupoId}/empresa/${empresaId}/proveedor/${proveedorId}/config`,
        );
    },
    { serializeError: serializeAxiosError },
);

/** Guarda asignación / config de proveedor-empresa (alias, alta, contactos, contadores). */
export const updateProveedorConfigEnGrupo = createAsyncThunk(
    'grupoEmpresarial/update_proveedor_config',
    async (
        {
            grupoId,
            empresaId,
            proveedorId,
            config,
        }: {
            grupoId: string | number;
            empresaId: string | number;
            proveedorId: string | number;
            config: Record<string, unknown>;
        },
        thunkAPI,
    ) => {
        const result = await axios.put<any>(
            `${apiUrl}/${grupoId}/empresa/${empresaId}/proveedor/${proveedorId}/config`,
            cleanEntity(config),
            { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
        );
        thunkAPI.dispatch(getProveedores(empresaId));
        return result;
    },
    { serializeError: serializeAxiosError },
);

/** Detalle de servicio asignado por idServicio. */
export const getServicioEnGrupo = createAsyncThunk(
    'grupoEmpresarial/get_servicio',
    async ({
        grupoId,
        empresaId,
        servicioId,
    }: {
        grupoId: string | number;
        empresaId: string | number;
        servicioId: string | number;
    }) => {
        return axios.get<any>(
            `${apiUrl}/${grupoId}/empresa/${empresaId}/servicio/${servicioId}`,
            { headers: { Accept: 'application/json' } },
        );
    },
    { serializeError: serializeAxiosError },
);

/** Completa / actualiza el servicio (fecha, facturación, precio, proveedor). */
export const updateServicioEnGrupo = createAsyncThunk(
    'grupoEmpresarial/update_servicio',
    async (
        {
            grupoId,
            empresaId,
            servicioId,
            body,
        }: {
            grupoId: string | number;
            empresaId: string | number;
            /** idServicio en edición; id de categoría al completar un alta nuevo. */
            servicioId: string | number;
            body: Record<string, unknown>;
        },
        thunkAPI,
    ) => {
        const result = await axios.put<any>(
            `${apiUrl}/${grupoId}/empresa/${empresaId}/servicio/${servicioId}`,
            cleanEntity(body),
            { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
        );
        thunkAPI.dispatch(getServicios(empresaId));
        return result;
    },
    { serializeError: serializeAxiosError },
);

/** Elimina un servicio asignado a la empresa en el grupo. */
export const deleteServicioEnGrupo = createAsyncThunk(
    'grupoEmpresarial/delete_servicio',
    async (
        {
            grupoId,
            empresaId,
            servicioId,
        }: {
            grupoId: string | number;
            empresaId: string | number;
            servicioId: string | number;
        },
        thunkAPI,
    ) => {
        const result = await axios.delete<any>(
            `${apiUrl}/${grupoId}/empresa/${empresaId}/servicio/${servicioId}`,
            { headers: { Accept: 'application/json' } },
        );
        thunkAPI.dispatch(getServicios(empresaId));
        return result;
    },
    { serializeError: serializeAxiosError },
);

/** Elimina la asignación / config proveedor-empresa en el grupo. */
export const deleteProveedorConfigEnGrupo = createAsyncThunk(
    'grupoEmpresarial/delete_proveedor_config',
    async ({
        grupoId,
        empresaId,
        proveedorId,
    }: {
        grupoId: string | number;
        empresaId: string | number;
        proveedorId: string | number;
    }) => {
        return axios.delete<any>(
            `${apiUrl}/${grupoId}/empresa/${empresaId}/proveedor/${proveedorId}/config`,
            { headers: { Accept: 'application/json' } },
        );
    },
    { serializeError: serializeAxiosError },
);

/**
 * Actualiza empresa vía …/config. Multipart alineado con curl:
 * `config` (JSON), opcionalmente `archivosSat` / `archivosSellos` (JSON mapa clave UUID →
 * `{ id, nombreArchivo, archivoSAT }`, misma forma en ambos) y partes archivo con nombre de campo = misma clave.
 */
export const updateEmpresaConfigEnGrupo = createAsyncThunk(
    'grupoEmpresarial/update_empresa_config_en_grupo',
    async ({
        grupoId,
        empresaId,
        config,
    }: {
        grupoId: string | number;
        empresaId: string | number;
        config: Record<string, unknown>;
    }) => {
        const fd = buildEmpresaConfigPutFormData(config);
        return axios.put<any>(`${apiUrl}/${grupoId}/empresa/${empresaId}/config`, fd);
    },
    { serializeError: serializeAxiosError },
);

export const importEmpresaConfigPdf = createAsyncThunk(
    'grupoEmpresarial/import_empresa_config_pdf',
    async (
        {
            grupoId,
            empresaId,
            pdfBytes,
        }: {
            grupoId: string | number;
            empresaId: string | number;
            pdfBytes: ArrayBuffer;
        },
        { rejectWithValue },
    ) => {
        const body = new Uint8Array(pdfBytes);
        try {
            return await axios.post<any>(`${apiUrl}/${grupoId}/empresa/${empresaId}/config`, body, {
                headers: { 'Content-Type': 'application/pdf' },
            });
        } catch (e) {
            if (isAxiosError(e) && e.response?.data != null) {
                return rejectWithValue({ data: e.response.data, status: e.response.status });
            }
            throw e;
        }
    },
    { serializeError: serializeAxiosError },
);

/** Importar PDF antes de crear la empresa (RFC / razón social). POST …/empresa/config */
export const importNuevaEmpresaConfigPdf = createAsyncThunk(
    'grupoEmpresarial/import_nueva_empresa_config_pdf',
    async ({ grupoId, pdfBytes }: { grupoId: string | number; pdfBytes: ArrayBuffer }, { rejectWithValue }) => {
        const body = new Uint8Array(pdfBytes);
        try {
            return await axios.post<any>(`${apiUrl}/${grupoId}/empresa/config`, body, {
                headers: { 'Content-Type': 'application/pdf' },
            });
        } catch (e) {
            if (isAxiosError(e) && e.response?.data != null) {
                return rejectWithValue({ data: e.response.data, status: e.response.status });
            }
            throw e;
        }
    },
    { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
    'grupoEmpresarial/fetch_entity',
    async (id: string | number) => {
        const requestUrl = `${apiUrl}/${id}`;
        return axios.get<any>(requestUrl);
    },
    { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
    'grupoEmpresarial/create_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.post<any>(apiUrl, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
    'grupoEmpresarial/update_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.put<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
    'grupoEmpresarial/partial_update_entity',
    async (entity: any, thunkAPI) => {
        const result = await axios.patch<any>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
    'grupoEmpresarial/delete_entity',
    async (id: string | number, thunkAPI) => {
        const requestUrl = `${apiUrl}/${id}`;
        const result = await axios.delete<any>(requestUrl);
        thunkAPI.dispatch(getEntities({}));
        return result;
    },
    { serializeError: serializeAxiosError },
);

// slice

export const GrupoEmpresarialSlice = createEntitySlice({
    name: 'grupoEmpresarial',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(resetProveedorConfig, state => {
                const s = state as unknown as GrupoEmpresarialState;
                s.proveedorConfig = null;
                s.proveedorConfigLoading = false;
                s.proveedorConfigProveedorId = null;
            })
            .addCase(getProveedorConfigEnGrupo.pending, state => {
                const s = state as unknown as GrupoEmpresarialState;
                s.proveedorConfigLoading = true;
            })
            .addCase(getProveedorConfigEnGrupo.fulfilled, (state, action) => {
                const s = state as unknown as GrupoEmpresarialState;
                s.proveedorConfigLoading = false;
                s.proveedorConfig = parseProveedorEmpresaConfig(action.payload.data);
                const pid = Number(action.meta.arg.proveedorId);
                s.proveedorConfigProveedorId = Number.isFinite(pid) ? pid : null;
            })
            .addCase(getProveedorConfigEnGrupo.rejected, state => {
                const s = state as unknown as GrupoEmpresarialState;
                s.proveedorConfigLoading = false;
                s.proveedorConfig = null;
                s.proveedorConfigProveedorId = null;
            })
            .addCase(getEntity.fulfilled, (state, action) => {
                state.loading = false;
                state.entity = action.payload.data;
            })
            .addCase(deleteEntity.fulfilled, state => {
                state.updating = false;
                state.updateSuccess = true;
                state.entity = {};
            })
            .addMatcher(isFulfilled(getEntities), (state, action) => {
                const { data, headers } = action.payload;
                return {
                    ...state,
                    loading: false,
                    entities: data,
                    totalItems: parseInt(headers['x-total-count'], 10),
                };
            })
            .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
                state.entity = action.payload.data;
            })
            .addMatcher(isFulfilled(getEmpresasByGrupo), (state, action) => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
                state.empresas = action.payload.data;
            })
            .addMatcher(isFulfilled(getProveedoresByGrupo), (state, action) => {
                const s = state as unknown as GrupoEmpresarialState;
                s.loading = false;
                s.proveedores = proveedoresFromApiPayload(action.payload);
            })
            .addMatcher(isFulfilled(createEmpresaEnGrupo, updateEmpresaEnGrupo, updateEmpresaConfigEnGrupo, updateProveedorConfigEnGrupo, deleteProveedorConfigEnGrupo, updateServicioEnGrupo, deleteServicioEnGrupo), state => {
                state.updating = false;
                state.loading = false;
                state.updateSuccess = true;
            })
            .addMatcher(isPending(getEntities, getEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.loading = true;
            })
            .addMatcher(isPending(getEmpresasByGrupo, getEntity), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.loading = true;
                state.empresas = [];
            })
            .addMatcher(isPending(getProveedoresByGrupo), state => {
                const s = state as unknown as GrupoEmpresarialState;
                s.errorMessage = null;
                s.loading = true;
            })

            .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity, createEmpresaEnGrupo, updateEmpresaEnGrupo, updateEmpresaConfigEnGrupo, updateProveedorConfigEnGrupo, deleteProveedorConfigEnGrupo, updateServicioEnGrupo, deleteServicioEnGrupo), state => {
                state.errorMessage = null;
                state.updateSuccess = false;
                state.updating = true;
            });
    },
});

export const { reset } = GrupoEmpresarialSlice.actions;

// Reducer
export default GrupoEmpresarialSlice.reducer;
